import React from 'react';
import { Cookie } from 'lucide-react';
import { APP_TRANSLATIONS } from '../constants/translations';
import { OPEN_CONSENT_EVENT, setConsent, shouldShowConsentBanner } from '../consent';

// Shown only to visitors in the consent region (see consent.js) until they
// choose, and again from the footer's "Cookies" link. Accept and Reject get
// equal weight on purpose: refusing has to be as easy as agreeing.
export default function CookieConsentBanner({ lang = 'en' }) {
  const [open, setOpen] = React.useState(shouldShowConsentBanner);
  const t = (key) => APP_TRANSLATIONS[lang]?.[key] || APP_TRANSLATIONS.en[key] || key;

  React.useEffect(() => {
    const handleOpen = () => setOpen(true);
    window.addEventListener(OPEN_CONSENT_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, handleOpen);
  }, []);

  if (!open) return null;

  const choose = (choice) => {
    setConsent(choice);
    setOpen(false);
  };

  const buttonClass = 'flex-1 rounded-full border px-4 py-2 text-sm font-semibold transition-colors';

  return (
    // Above the feedback widget (z-150), which sits in the same bottom area.
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-text"
      className="fixed inset-x-3 bottom-3 z-[160] sm:inset-x-auto sm:left-5 sm:bottom-5 sm:w-[22rem]"
    >
      <div className="rounded-2xl border border-white/15 bg-slate-900/95 p-4 shadow-[0_24px_80px_rgba(2,6,23,0.6)] ring-1 ring-cyan-400/10 backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <Cookie size={18} className="text-cyan-300" aria-hidden="true" />
          <h2 id="cookie-consent-title" className="text-sm font-bold text-white">{t('cookieBannerTitle')}</h2>
        </div>
        <p id="cookie-consent-text" className="mt-2 text-sm leading-6 text-ink">{t('cookieBannerText')}</p>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => choose('denied')}
            className={`${buttonClass} border-white/15 bg-white/5 text-ink hover:bg-white/10 hover:text-white`}
          >
            {t('cookieReject')}
          </button>
          <button
            type="button"
            onClick={() => choose('granted')}
            className={`${buttonClass} border-cyan-400/40 bg-cyan-500/15 text-cyan-100 hover:bg-cyan-500/25 hover:text-white`}
          >
            {t('cookieAccept')}
          </button>
        </div>
      </div>
    </div>
  );
}
